// src/components/admin/ChangeLocation.jsx

import React, { useState }         from 'react';
import { useChangeLocation }       from '../../hooks/useChangeLocation';
import { CSVLink }                 from 'react-csv';
import PermissionDenied            from '../common/PermissionDenied';
import ToastNotification           from '../common/ToastNotification';
import LoadingSpinner              from '../common/LoadingSpinner';
import { useAuth }                 from '../../contexts/AuthContext';



import {
  Box, Container, Typography, Paper, Button, Grid,
  TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, IconButton,
  Chip, Alert, InputAdornment, Tooltip, Divider,
  Tab, Tabs, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Fade, Skeleton, Checkbox, FormControlLabel,
  List, ListItem, ListItemText, ListItemIcon,
  FormHelperText, Popper, ClickAwayListener,
  Menu,
} from '@mui/material';

import SearchIcon       from '@mui/icons-material/Search';
import ClearIcon        from '@mui/icons-material/Clear';
import LocationOnIcon   from '@mui/icons-material/LocationOn';
import SwapHorizIcon    from '@mui/icons-material/SwapHoriz';
import HistoryIcon      from '@mui/icons-material/History';
import UndoIcon         from '@mui/icons-material/Undo';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteSweepIcon  from '@mui/icons-material/DeleteSweep';
import SendIcon         from '@mui/icons-material/Send';
import Inventory2Icon   from '@mui/icons-material/Inventory2';
import InventoryIcon    from '@mui/icons-material/Inventory';
import AccountTreeIcon  from '@mui/icons-material/AccountTree';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FilterListIcon   from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// ── Asset Searchable Select ───────────────────────────────────────────────────
const AssetSearchableSelect = ({
  value,
  onChange,
  options = [],
  allAssets = [],
  disabled = false,
  loading = false,
  placeholder = 'Search by name or asset ID...',
}) => {
  const [open, setOpen]     = React.useState(false);
  const [query, setQuery]   = React.useState('');
  const anchorRef           = React.useRef(null);
  const inputRef            = React.useRef(null);

  const selected = React.useMemo(
    () => allAssets.find((a) => a.assetId === value) || null,
    [allAssets, value],
  );

  const filtered = React.useMemo(() => {
    const q = String(query || '').toLowerCase().trim();
    if (!q) return options;
    return options.filter((a) => {
      const id   = String(a?.assetId   ?? '').toLowerCase();
      const name = String(a?.assetName ?? '').toLowerCase();
      return id.includes(q) || name.includes(q);
    });
  }, [options, query]);

  const handleFocus = () => {
    if (!disabled && !loading) setOpen(true);
  };

  const handleSelect = (asset) => {
    onChange(asset.assetId);
    setOpen(false);
    setQuery('');
    if (inputRef.current) inputRef.current.blur();
  };

  const handleClickAway = (e) => {
    if (anchorRef.current && anchorRef.current.contains(e.target)) return;
    setOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
      if (inputRef.current) inputRef.current.blur();
    }
  };

  const displayValue = open
    ? query
    : selected
      ? `${selected.assetName} (${selected.assetId})`
      : '';

  return (
    <Box
      ref={anchorRef}
      sx={{ position: 'relative', width: '100%' }}
      onKeyDown={handleKeyDown}
    >
      <TextField
        inputRef={inputRef}
        fullWidth
        size="small"
        label="Select Asset"
        placeholder={selected ? '' : placeholder}
        value={displayValue}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={handleFocus}
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'action.active' }} fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {open && query ? (
                <IconButton
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setQuery('')}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null}
            </InputAdornment>
          ),
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
      />

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{
          zIndex: 1300,
          width: anchorRef.current ? anchorRef.current.offsetWidth : undefined,
        }}
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            elevation={6}
            sx={{
              mt: 0.5,
              maxHeight: 320,
              overflowY: 'auto',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              '&::-webkit-scrollbar':       { width: 8 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.15)', borderRadius: 4 },
            }}
          >
            {filtered.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {query
                    ? 'No assets match your search'
                    : allAssets.length === 0
                      ? 'No assets available'
                      : 'Type to search...'}
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {filtered.map((asset) => {
                  const isSelected = asset.assetId === value;
                  return (
                    <ListItem
                      key={asset.id || asset.assetId}
                      button
                      onClick={() => handleSelect(asset)}
                      sx={{
                        py: 0.9,
                        px: 2,
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                          width: '100%',
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {asset.assetName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          ({asset.assetId})
                        </Typography>

                        {asset.assetType && (
                          <Chip
                            label={asset.assetType}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
                          />
                        )}

                        {asset.ownershipType && (
                          <Chip
                            label={asset.ownershipType}
                            size="small"
                            variant="outlined"
                            color="secondary"
                            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
                          />
                        )}

                     {(String(asset.status || '').toLowerCase() === 'disposed' || asset.isDisposed) && (
  <Chip
    label="DISPOSED"
    size="small"
    color="error"
    sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
  />
)}

                      {String(asset.serializationType || '').toLowerCase() === 'non-serialized' && (
  <Chip
    label="QTY"
    size="small"
    color="info"
    variant="outlined"
    sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
  />
)}
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

// ── Skeleton ──────────────────────────────────────────────────────────────────
const ChangeLocationSkeleton = () => (
  <Container
    maxWidth="xl"
    sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}
  >
    <Box>
      <Skeleton variant="text" width={320} height={44} sx={{ borderRadius: 1 }} />
      <Skeleton variant="text" width={420} height={20} sx={{ borderRadius: 1, mt: 0.5 }} />
    </Box>
    <Paper sx={{ p: 2.5, borderRadius: 3 }} variant="outlined">
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <Skeleton variant="rounded" height={40} sx={{ borderRadius: 2 }} />
        </Grid>
        <Grid item xs={12} md={5}>
          <Skeleton variant="rounded" height={40} sx={{ borderRadius: 2 }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Skeleton variant="text" width="60%" height={20} sx={{ borderRadius: 1 }} />
        </Grid>
      </Grid>
    </Paper>
    <Paper variant="outlined" sx={{ borderRadius: 3, p: 8, textAlign: 'center' }}>
      <Skeleton variant="circular" width={56} height={56} sx={{ mx: 'auto', mb: 2 }} />
      <Skeleton variant="text" width={220} height={28} sx={{ mx: 'auto', borderRadius: 1 }} />
      <Skeleton variant="text" width={340} height={20} sx={{ mx: 'auto', borderRadius: 1, mt: 0.5 }} />
    </Paper>
  </Container>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const ChangeLocation = () => {
  const { currentUser } = useAuth();

  const {
    filteredAssets,
    filteredLocations,
    foundAsset,
    reversedHistory,
    loading,
    authLoading,
    isLoadingLocations,
    isPreparingExport,
    permissionError,
    notification,
    pageReady,

    assetTypeOptions,
    ownershipTypeOptions,
    isLoadingPreferences,

    isPrimaryAsset,
    isConsumable,
    isSparePart,
    isExcludedFromMove,
    canHaveChildren,

    childAssets,
    selectedChildIds,
    isLoadingChildren,
    showChildMovePanel,
    allChildrenSelected,

    isAssetDisposed,
    isNonSerialized,
    searchTerm,          setSearchTerm,
    selectedLocation,    setSelectedLocation,
    changeReason,        setChangeReason,
    quantityToMove,      setQuantityToMove,
    activeTab,           setActiveTab,
    historyPage,         setHistoryPage,
    historyRowsPerPage,  setHistoryRowsPerPage,
    setIsPreparingExport,

    filterAssetType,     setFilterAssetType,
    filterOwnershipType, setFilterOwnershipType,

    handleAssetSelect,
    handleChangeLocation,
    handleUndoLastChange,
    handleToggleChildAsset,
    handleToggleAllChildren,
    exportToPDF,
    exportToCSV,
    setNotification,
    allAssets,
    selectedAssetId,
  } = useChangeLocation();

  // overallLoading is ONLY used for disabling controls, not for showing spinners
  const overallLoading = loading || authLoading;

  // ── Export menu state ─────────────────────────────────────────────────────
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);

  const handleExportMenuOpen  = (e) => setExportMenuAnchor(e.currentTarget);
  const handleExportMenuClose = ()  => setExportMenuAnchor(null);

  const handleExportPDFClick = () => {
    handleExportMenuClose();
    exportToPDF();
  };

  // ── Guards ────────────────────────────────────────────────────────────────
  if (permissionError) {
    return (
      <Box sx={{ p: 4 }}>
        <PermissionDenied
          message={permissionError}
          userId={currentUser?.uid}
          userEmail={currentUser?.email}
        />
      </Box>
    );
  }
  if (!pageReady) return <ChangeLocationSkeleton />;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Container
      maxWidth="xl"
      sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      {notification && (
        <ToastNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Header */}
      <Box>
        <Typography
          variant="h4" component="h1"
          sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.5px' }}
        >
          Asset Location Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Transfer assets between locations, track movement history and audit trail
        </Typography>
      </Box>

      {/* Filter Assets */}
      <Paper sx={{ p: 2, borderRadius: 3 }} variant="outlined">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <FilterListIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
            Filter Assets
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" disabled={isLoadingPreferences}>
              <InputLabel>Asset Type</InputLabel>
              <Select
                value={filterAssetType}
                label="Asset Type"
                onChange={(e) => setFilterAssetType(e.target.value)}
                sx={{ borderRadius: 2 }}
                MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { cursor: 'pointer' } } } }}
              >
                <MenuItem value="">
                  {isLoadingPreferences
                    ? 'Loading...'
                    : assetTypeOptions.length === 0
                    ? 'No Asset Types Configured'
                    : 'All Types'}
                </MenuItem>
                {assetTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {!isLoadingPreferences && assetTypeOptions.length === 0 && (
                <FormHelperText>
                  Create Asset Type preferences to enable this filter
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" disabled={isLoadingPreferences}>
              <InputLabel>Ownership Type</InputLabel>
              <Select
                value={filterOwnershipType}
                label="Ownership Type"
                onChange={(e) => setFilterOwnershipType(e.target.value)}
                sx={{ borderRadius: 2 }}
                MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { cursor: 'pointer' } } } }}
              >
                <MenuItem value="">
                  {isLoadingPreferences
                    ? 'Loading...'
                    : ownershipTypeOptions.length === 0
                    ? 'No Ownership Types Configured'
                    : 'All Ownership Types'}
                </MenuItem>
                {ownershipTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {!isLoadingPreferences && ownershipTypeOptions.length === 0 && (
                <FormHelperText>
                  Create Ownership Type preferences to enable this filter
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {(filterAssetType || filterOwnershipType) && (
            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setFilterAssetType('');
                  setFilterOwnershipType('');
                }}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Clear Filters
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Asset Selector */}
      <Paper sx={{ p: 2.5, borderRadius: 3 }} variant="outlined">
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={8}>
            <AssetSearchableSelect
              value={selectedAssetId}
              onChange={(assetId) => handleAssetSelect(assetId)}
              options={filteredAssets}
              allAssets={allAssets}
              disabled={overallLoading || allAssets.length === 0}
              loading={false}
              placeholder="Search by name or asset ID..."
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Asset Details */}
      {foundAsset?.assetId ? (
        <Fade in={Boolean(foundAsset)}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {isAssetDisposed && (
              <Alert severity="warning" icon={<DeleteSweepIcon />} sx={{ borderRadius: 2.5 }}>
                <Typography variant="body2" fontWeight={600}>Asset Disposed</Typography>
                <Typography variant="caption" display="block">
                  This asset has been disposed and cannot be moved to a new location.
                </Typography>
              </Alert>
            )}

            {isExcludedFromMove && !isAssetDisposed && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ borderRadius: 2.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {foundAsset.assetType} — Location Change Not Applicable
                </Typography>
                <Typography variant="caption" display="block">
                  {foundAsset.assetType}s are managed through the{' '}
                  <strong>Consumptions module</strong> and are not moved via this workflow.
                </Typography>
              </Alert>
            )}

            {isNonSerialized && !isAssetDisposed && !isExcludedFromMove && (
              <Alert severity="info" icon={<Inventory2Icon />} sx={{ borderRadius: 2.5 }}>
                <Typography variant="body2" fontWeight={600}>Quantity-Based Asset</Typography>
                <Typography variant="caption" display="block">
                  Available:{' '}
                  <strong>{foundAsset.quantity || 0}</strong>{' '}
                  {foundAsset.unitOfMeasurement || 'units'}
                </Typography>
              </Alert>
            )}

            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
                <Typography variant="h6" fontWeight={700}>Asset Details</Typography>
              </Box>
              <Divider />
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2.5}>
                  {[
                    { label: 'Asset ID',   value: foundAsset.assetId,   mono: true },
                    { label: 'Asset Name', value: foundAsset.assetName              },
                    {
  label:     'Status',
  value:     foundAsset.status || '—',
  chip:      true,
  chipColor: isAssetDisposed ? 'error' : 'success',
},
                    {
                      label: 'Tracking Type',
                      value: isNonSerialized
                        ? `Quantity-Based (${foundAsset.quantity || 0} ${foundAsset.unitOfMeasurement || 'units'})`
                        : 'Individual Item',
                    },
                    ...(!isNonSerialized && foundAsset.manufacturerSerialNumber
                      ? [{ label: 'Manufacturer S/N', value: foundAsset.manufacturerSerialNumber, mono: true }]
                      : []),
                    {
                      label:     'Asset Type',
                      value:     foundAsset.assetType || '—',
                      chip:      true,
                      chipColor: 'primary',
                    },
                    {
                      label:     'Ownership Type',
                      value:     foundAsset.ownershipType || '—',
                      chip:      true,
                      chipColor: 'secondary',
                    },
                    ...(foundAsset.parentAssetId
                      ? [{ label: 'Parent Asset ID', value: foundAsset.parentAssetId, mono: true }]
                      : []),
                    {
                      label: 'Current Location',
                      value: foundAsset.currentLocationName ?? 'N/A',
                      icon:  <LocationOnIcon fontSize="small" color="primary" />,
                    },
                    { label: 'Last Updated By', value: foundAsset.updatedBy ?? 'N/A' },
                    {
                      label: 'Last Updated At',
                      value:
                        foundAsset.updatedAt instanceof Date
                          ? foundAsset.updatedAt.toLocaleString()
                          : (foundAsset.updatedAt?.toDate?.()?.toLocaleString?.() ?? 'N/A'),
                    },
                  ].map((item, idx) => (
                    <Grid item xs={12} sm={6} md={3} key={idx}>
                      <Typography
                        variant="caption" color="text.secondary" fontWeight={700}
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 0.5 }}
                      >
                        {item.label}
                      </Typography>
                      {item.chip ? (
                        <Chip
                          label={item.value} size="small" color={item.chipColor}
                          sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {item.icon && item.icon}
                          <Typography
                            variant="body2" fontWeight={600} color="text.primary"
                            sx={{
                              fontFamily: item.mono ? 'monospace' : 'inherit',
                              fontSize:   item.mono ? '0.85rem' : 'inherit',
                            }}
                          >
                            {item.value || '—'}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Paper>

            {canHaveChildren && showChildMovePanel && (
              <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Box
                  sx={{
                    px: 3, py: 2,
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', gap: 1,
                    bgcolor: 'primary.50',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountTreeIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      Linked Child Assets
                    </Typography>
                    <Chip
                      label={`${childAssets.length} linked`}
                      size="small" color="primary" variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Select which child assets to move along with the Primary Asset
                  </Typography>
                </Box>
                <Divider />

                {isLoadingChildren ? (
                  <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Loading linked assets...
                    </Typography>
                  </Box>
                ) : childAssets.length === 0 ? (
                  <Box sx={{ p: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No linked Sub Assets, Components, or Accessories found.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allChildrenSelected}
                          indeterminate={
                            selectedChildIds.length > 0 && !allChildrenSelected
                          }
                          onChange={handleToggleAllChildren}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" fontWeight={700}>
                          {allChildrenSelected ? 'Deselect All' : 'Select All'}
                        </Typography>
                      }
                      sx={{ mb: 1, ml: 0 }}
                    />
                    <Divider sx={{ mb: 1 }} />

                    <List dense disablePadding>
                      {childAssets.map((child) => (
                        <ListItem
                          key={child.id}
                          disableGutters
                          sx={{
                            py: 0.5, px: 1, borderRadius: 1.5,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              edge="start"
                              size="small"
                              checked={selectedChildIds.includes(child.id)}
                              onChange={() => handleToggleChildAsset(child.id)}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {child.assetName}
                                </Typography>
                                <Typography
                                  variant="caption" color="text.secondary"
                                  sx={{ fontFamily: 'monospace' }}
                                >
                                  ({child.assetId})
                                </Typography>
                                <Chip
                                  label={child.assetType}
                                  size="small" variant="outlined" color="primary"
                                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
                                />
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                Current: {child.currentLocationName || 'N/A'}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>

                    {selectedChildIds.length > 0 && (
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="primary" fontWeight={600}>
                          {selectedChildIds.length} of {childAssets.length} child asset
                          {childAssets.length !== 1 ? 's' : ''} will be moved
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            )}

            {/* Tabs */}
            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                  px: 2, bgcolor: 'background.default',
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 52 },
                }}
              >
                <Tab icon={<SwapHorizIcon />} iconPosition="start" label="Change Location" />
                <Tab
                  icon={<HistoryIcon />} iconPosition="start"
                  label={`Location History (${foundAsset.locationHistory?.length || 0})`}
                />
              </Tabs>
              <Divider />

              {activeTab === 0 && (
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>New Location</InputLabel>
                        <Select
                          value={selectedLocation}
                          label="New Location"
                          onChange={(e) => setSelectedLocation(e.target.value)}
                          disabled={
                            isLoadingLocations ||
                            isAssetDisposed    ||
                            isExcludedFromMove
                          }
                          sx={{ borderRadius: 2, cursor: 'pointer' }}
                          MenuProps={{
                            PaperProps: {
                              sx: { '& .MuiMenuItem-root': { cursor: 'pointer' } },
                            },
                          }}
                        >
                          <MenuItem value="">
                            {isLoadingLocations
                              ? 'Loading locations...'
                              : filteredLocations.length === 0
                              ? 'No locations available'
                              : 'Select Location'}
                          </MenuItem>
                          {filteredLocations.map((loc) => (
                            <MenuItem key={loc.id} value={loc.locationCode}>
                              {loc.locationName} — {loc.city}, {loc.state}
                              {loc.locationCode && ` (${loc.locationCode})`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {isNonSerialized && !isAssetDisposed && !isExcludedFromMove && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth size="small" type="number"
                          label="Quantity to Move *"
                          value={quantityToMove}
                          onChange={(e) =>
                            setQuantityToMove(Math.max(1, parseInt(e.target.value) || 1))
                          }
                          inputProps={{ min: 1, max: foundAsset.quantity || 1 }}
                          helperText={`Available: ${foundAsset.quantity || 0} ${foundAsset.unitOfMeasurement || 'units'}`}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <TextField
                        fullWidth size="small" multiline rows={3}
                        label="Reason for Location Change"
                        placeholder="E.g., Asset moved due to department change"
                        value={changeReason}
                        onChange={(e) => setChangeReason(e.target.value)}
                        disabled={isAssetDisposed || isExcludedFromMove}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained" color="primary"
                          startIcon={
                            overallLoading
                              ? <CircularProgress size={18} color="inherit" />
                              : <SendIcon />
                          }
                          onClick={handleChangeLocation}
                          disabled={
                            overallLoading        ||
                            !selectedLocation     ||
                            !changeReason.trim()  ||
                            isLoadingLocations    ||
                            isAssetDisposed       ||
                            isExcludedFromMove    ||
                            (isNonSerialized && (
                              quantityToMove < 1 ||
                              quantityToMove > (foundAsset.quantity || 0)
                            ))
                          }
                          sx={{
                            borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3,
                            opacity: isAssetDisposed || isExcludedFromMove ? 0.5 : 1,
                            cursor:  isAssetDisposed || isExcludedFromMove
                              ? 'not-allowed'
                              : 'pointer',
                          }}
                          disableElevation
                        >
                          {isAssetDisposed
                            ? 'Cannot Move Disposed Asset'
                            : isExcludedFromMove
                            ? `${foundAsset.assetType}s use Consumptions Module`
                            : overallLoading
                            ? 'Updating...'
                            : `Change Location${
                                isNonSerialized
                                  ? ` (${quantityToMove} ${foundAsset.unitOfMeasurement || 'units'})`
                                  : ''
                              }${
                                canHaveChildren && selectedChildIds.length > 0                                  ? ` + ${selectedChildIds.length} child asset(s)`
                                  : ''
                              }`}
                        </Button>

                        {foundAsset.locationHistory?.length > 0 && (
                          <Button
                            variant="outlined" color="warning"
                            startIcon={<UndoIcon />}
                            onClick={handleUndoLastChange}
                            disabled={overallLoading || isAssetDisposed}
                            sx={{
                              borderRadius: 2, textTransform: 'none', fontWeight: 600,
                              opacity: isAssetDisposed ? 0.5 : 1,
                              cursor:  isAssetDisposed ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {isAssetDisposed ? 'Cannot Undo (Disposed)' : 'Undo Last Change'}
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeTab === 1 && (
                <Box sx={{ p: 0 }}>
                  <Box
                    sx={{
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', flexWrap: 'wrap', gap: 1.5,
                      px: 3, py: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      Movement History
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined" color="primary" size="small"
                        startIcon={<FileDownloadIcon />}
                        endIcon={<KeyboardArrowDownIcon />}
                        onClick={handleExportMenuOpen}
                        disabled={isPreparingExport || !foundAsset.locationHistory?.length}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {isPreparingExport ? 'Preparing...' : 'Export'}
                      </Button>

                      <Menu
                        anchorEl={exportMenuAnchor}
                        open={exportMenuOpen}
                        onClose={handleExportMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{
                          sx: {
                            mt: 0.5,
                            minWidth: 200,
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
                            '& .MuiMenuItem-root': { cursor: 'pointer' },
                          },
                        }}
                      >
                        <CSVLink
                          {...exportToCSV()}
                          filename={`${foundAsset.assetId}_location_history.csv`}
                          onClick={handleExportMenuClose}
                          style={{
                            textDecoration: 'none',
                            color: 'inherit',
                            display: 'block',
                          }}
                        >
                          <MenuItem>
                            <ListItemIcon>
                              <FileDownloadIcon fontSize="small" color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Export CSV"
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                              }}
                            />
                          </MenuItem>
                        </CSVLink>

                        <MenuItem onClick={handleExportPDFClick}>
                          <ListItemIcon>
                            <PictureAsPdfIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="Export PDF"
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                              fontWeight: 500,
                            }}
                          />
                        </MenuItem>
                      </Menu>
                    </Box>
                  </Box>

                  <Divider />

                  <TableContainer
                    sx={{
                      overflowX: 'auto',
                      '&::-webkit-scrollbar':       { height: 8 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.12)', borderRadius: 4 },
                    }}
                  >
                    {reversedHistory.length === 0 ? (
                      <Box sx={{ p: 8, textAlign: 'center' }}>
                        <Alert severity="info" sx={{ justifyContent: 'center', borderRadius: 2.5 }}>
                          No location history available for this asset.
                        </Alert>
                      </Box>
                    ) : (
                      <Table sx={{ minWidth: 800 }} size="medium">
                        <TableHead>
                          <TableRow>
                            {[
                              'S.No', 'From Location', 'To Location',
                              'Changed By', 'Change Date',
                              ...(isNonSerialized ? ['Quantity Moved'] : []),
                              'Reason',
                            ].map((h, idx) => (
                              <TableCell
                                key={idx}
                                align={h === 'S.No' ? 'center' : 'left'}
                                sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                              >
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reversedHistory
                            .slice(
                              historyPage * historyRowsPerPage,
                              historyPage * historyRowsPerPage + historyRowsPerPage,
                            )
                            .map((entry, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell align="center">
                                  <Typography
                                    variant="body2" fontWeight={600} color="text.secondary"
                                  >
                                    {historyPage * historyRowsPerPage + idx + 1}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Tooltip title={entry.from || ''} arrow>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                      <LocationOnIcon fontSize="small" color="error" sx={{ fontSize: 16 }} />
                                      <Typography
                                        variant="body2"
                                        sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                      >
                                        {entry.from || '—'}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  <Tooltip title={entry.to || ''} arrow>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                      <LocationOnIcon fontSize="small" color="success" sx={{ fontSize: 16 }} />
                                      <Typography
                                        variant="body2" fontWeight={500}
                                        sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                      >
                                        {entry.to || '—'}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2" color="text.secondary"
                                    sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                                  >
                                    {entry.changedBy || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {entry.changedAt instanceof Date
                                      ? entry.changedAt.toLocaleString()
                                      : (entry.changedAt?.toDate?.()?.toLocaleString?.() ?? 'N/A')}
                                  </Typography>
                                </TableCell>
                                {isNonSerialized && (
                                  <TableCell>
                                    <Chip
                                      label={`${entry.quantityMoved || 'Full'} ${foundAsset.unitOfMeasurement || 'units'}`}
                                      size="small" color="info" variant="outlined"
                                      sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                                    />
                                  </TableCell>
                                )}
                                <TableCell sx={{ maxWidth: 220 }}>
                                  <Tooltip
                                    title={entry.reason || ''} arrow
                                    disableHoverListener={!entry.reason || entry.reason.length <= 40}
                                  >
                                    <Typography
                                      variant="body2" color="text.secondary"
                                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}
                                    >
                                      {entry.reason || '—'}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    )}
                  </TableContainer>

                  {reversedHistory.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        py: 1.5, px: 2, borderTop: '1px solid', borderColor: 'divider',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Showing {historyPage * historyRowsPerPage + 1} to{' '}
                        {Math.min(
                          (historyPage + 1) * historyRowsPerPage,
                          reversedHistory.length,
                        )}{' '}
                        of {reversedHistory.length} entries
                      </Typography>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={reversedHistory.length}
                        rowsPerPage={historyRowsPerPage}
                        page={historyPage}
                        onPageChange={(_, newPage) => setHistoryPage(newPage)}
                        onRowsPerPageChange={(e) => {
                          setHistoryRowsPerPage(parseInt(e.target.value, 10));
                          setHistoryPage(0);
                        }}
                        sx={{ border: 'none', overflow: 'hidden' }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          </Box>
        </Fade>
      ) : (
        /* Empty State — no spinner, just a hint */
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 8, textAlign: 'center' }}>
          <Box>
            <InventoryIcon sx={{ fontSize: 56, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              Select an asset to begin
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
              Use the search box above to find and select an asset for location management
            </Typography>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default ChangeLocation;